'use client';

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocale, useTranslations } from 'next-intl';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Star,
  ShieldCheck,
  Languages,
  ImageOff,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toImageItems } from '@/lib/media';
import { REVIEWS, type Review } from '@/lib/reviews';
import FadeInSection from '@/components/ui/FadeInSection';
import Lightbox from '@/components/ui/Lightbox';

const STAR_GOLD = '#f7c14b'; // matches the site's golden CTA gradient

// Center (main) card background — frosted sage glass: translucent sage gradient
// with a backdrop blur, so the dark forest atmosphere shows through faintly.
const CENTER_CARD_BG: CSSProperties = {
  backgroundImage:
    'linear-gradient(145deg, rgba(49,54,53,0.92) 0%, rgba(28,33,32,0.94) 100%)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
};
const NAV_SPACE = 80; // clearance under the cards for the prev/next controls

// Reviews that come with project photos — the initial center is always picked
// from these so the opening card showcases real work.
const IMAGE_REVIEW_IDS = REVIEWS.filter((r) => r.images.length > 0).map((r) => r.id);

// Rotating list: each card keeps a stable React `key` so position changes animate;
// only the card that wraps around the ends gets a fresh key (remounts off-screen).
type Card = Review & { key: number };

// Index of the slot whose stagger position is 0 (the visible center).
const CENTER_INDEX = REVIEWS.length % 2 ? (REVIEWS.length + 1) / 2 : REVIEWS.length / 2;

// Cyclically rotate REVIEWS so `targetId` lands in the center slot, assigning a
// fresh contiguous key block starting at `startKey`.
function rotateToCenter(targetId: number, startKey: number): Card[] {
  const n = REVIEWS.length;
  const xi = REVIEWS.findIndex((r) => r.id === targetId);
  const offset = ((xi - CENTER_INDEX) % n + n) % n;
  return REVIEWS.map((_, i) => ({ ...REVIEWS[(i + offset) % n], key: startKey + i }));
}

// useLayoutEffect warns during SSR; fall back to useEffect on the server.
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function clampStyle(lines: number): CSSProperties {
  return {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lines,
    overflow: 'hidden',
  };
}

// Five stars filled to `score` — supports fractions (4.5 → one half-gold star)
// by overlaying a width-clipped gold star over a faint base star.
function Stars({ score = 5, size = 16 }: { score?: number; size?: number }) {
  return (
    <div
      className="flex gap-0.5"
      role="img"
      aria-label={`${score} / 5`}
      style={{ color: STAR_GOLD }}
    >
      {Array.from({ length: 5 }).map((_, i) => {
        const fill = Math.max(0, Math.min(1, score - i));
        return (
          <span
            key={i}
            className="relative inline-block shrink-0"
            style={{ width: size, height: size }}
          >
            <Star
              size={size}
              strokeWidth={0}
              className="absolute inset-0 fill-current"
              style={{ opacity: 0.28 }}
            />
            <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
              <Star size={size} strokeWidth={0} className="fill-current" />
            </span>
          </span>
        );
      })}
    </div>
  );
}

// A hovered thumbnail surfaces this floating, enlarged preview so the work is
// readable without opening the full lightbox.
type Preview = { src: string; rect: DOMRect };

function ReviewThumb({
  src,
  alt,
  onClick,
  onPreview,
}: {
  src: string;
  alt: string;
  onClick: (e: React.MouseEvent) => void;
  onPreview: (preview: Preview | null) => void;
}) {
  const [error, setError] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      // Hover (mouse only — gated upstream) → large floating preview. Cleared on
      // leave and on click (the click opens the full-screen lightbox instead).
      onMouseEnter={(e) =>
        !error && onPreview({ src, rect: e.currentTarget.getBoundingClientRect() })
      }
      onMouseLeave={() => onPreview(null)}
      aria-label={alt}
      className="relative h-11 w-11 shrink-0 overflow-hidden sm:h-14 sm:w-14 rounded-sm border border-on-surface/20 ring-1 ring-inset ring-on-surface/10 transition-transform hover:scale-[1.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-on-surface"
    >
      {error ? (
        <span className="flex h-full w-full items-center justify-center bg-on-surface/10 text-on-surface/40">
          <ImageOff size={18} />
        </span>
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={encodeURI(src)}
          alt={alt}
          onError={() => setError(true)}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
        />
      )}
    </button>
  );
}

// Enlarged thumbnail preview that floats above the hovered thumb. Portaled to
// <body> (the carousel clips overflow) and positioned from the thumb's viewport
// rect: above it when there's room, otherwise below, clamped to the viewport.
// pointer-events-none so it never swallows the click that opens the lightbox.
function HoverPreview({ src, rect, alt }: { src: string; rect: DOMRect; alt: string }) {
  const [shown, setShown] = useState(false);
  useEffect(() => setShown(true), []);

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const pw = Math.min(340, vw - 24);
  const ph = Math.round(pw * 0.72);
  const left = Math.max(12, Math.min(rect.left + rect.width / 2 - pw / 2, vw - pw - 12));
  const above = rect.top - ph - 14;
  const top = above >= 12 ? above : Math.min(rect.bottom + 14, vh - ph - 12);

  return (
    <div
      aria-hidden
      style={{ left, top, width: pw, height: ph, transformOrigin: 'center bottom' }}
      className={cn(
        'pointer-events-none fixed z-[100] overflow-hidden rounded-[2rem] border border-on-surface/40 bg-surface-container ring-1 ring-black/40 transition duration-150 ease-out',
        shown ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
      )}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={encodeURI(src)} alt={alt} draggable={false} loading="lazy" decoding="async" className="h-full w-full object-cover" />
    </div>
  );
}

// A pill control used on the (sage) center card — show more / translate.
function CardChip({
  onClick,
  children,
}: {
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-on-surface/25 bg-on-surface/5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-on-surface/90 transition-colors hover:border-on-surface/50 hover:bg-on-surface/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-on-surface"
    >
      {children}
    </button>
  );
}

// Renders the active label while reserving the width of the LONGER of the two
// toggle labels (overlaid, invisible) so the chip never resizes when it flips —
// e.g. "Show more" ↔ "Show less" / "Translate" ↔ "Show original".
function SwapLabel({ active, other }: { active: string; other: string }) {
  const longer = active.length >= other.length ? active : other;
  return (
    <span className="grid justify-items-center">
      <span aria-hidden className="invisible col-start-1 row-start-1 whitespace-nowrap">
        {longer}
      </span>
      <span className="col-start-1 row-start-1 whitespace-nowrap">{active}</span>
    </span>
  );
}

interface CardProps {
  review: Review;
  position: number;
  cardW: number;
  cardH: number;
  display: string;
  collapseLines: number;
  isExpanded: boolean;
  isTranslated: boolean;
  canTranslate: boolean;
  showToggle: boolean;
  t: ReturnType<typeof useTranslations>;
  scrollAreaRef: RefObject<HTMLDivElement>;
  quoteRef: RefObject<HTMLParagraphElement>;
  onSelect: () => void;
  onToggleExpand: () => void;
  onToggleTranslate: () => void;
  onOpenLightbox: (images: string[], index: number) => void;
  onPreview: (preview: Preview | null) => void;
}

function TestimonialCard({
  review,
  position,
  cardW,
  cardH,
  display,
  collapseLines,
  isExpanded,
  isTranslated,
  canTranslate,
  showToggle,
  t,
  scrollAreaRef,
  quoteRef,
  onSelect,
  onToggleExpand,
  onToggleTranslate,
  onOpenLightbox,
  onPreview,
}: CardProps) {
  const isCenter = position === 0;
  const dist = Math.abs(position);
  const hidden = dist > 2;

  const tx = position * cardW * 0.7;
  const baseTy = -NAV_SPACE / 2; // lift the stack so the nav controls clear it
  const ty = baseTy + (isCenter ? 0 : position % 2 ? 20 : -20);
  const rot = isCenter ? 0 : position % 2 ? 2 : -2;
  const scale = isCenter ? 1 : 0.88;

  return (
    <div
      onClick={isCenter ? undefined : onSelect}
      aria-hidden={hidden || undefined}
      className={cn(
        'absolute left-1/2 top-1/2 flex flex-col rounded-md border p-6 transition-all duration-500 ease-in-out motion-reduce:transition-none sm:p-7',
        isCenter
          ? 'forest-img-shadow cursor-default border-primary/60 text-on-surface'
          : 'forest-card cursor-pointer border-transparent text-on-surface hover:opacity-90',
      )}
      style={{
        width: cardW,
        height: cardH,
        zIndex: 30 - dist * 10,
        transform: `translate(-50%, -50%) translateX(${tx}px) translateY(${ty}px) scale(${scale}) rotate(${rot}deg)`,
        opacity: hidden ? 0 : isCenter ? 1 : 0.42,
        pointerEvents: hidden ? 'none' : 'auto',
        ...(isCenter ? CENTER_CARD_BG : null),
      }}
    >
      <Stars score={review.score} />

      {/* Quote — fixed card height: collapsed clamps to "…"; expanded scrolls.
          overflow-y-auto shows the scrollbar ONLY when expanded (genuinely
          scrollable) — collapsed content fits, so no bar and no arrow buttons.
          .nice-scrollbar reserves the gutter (scrollbar-gutter: stable) so the
          text width never changes when the bar appears. */}
      <div
        ref={isCenter ? scrollAreaRef : undefined}
        className={cn(
          'mt-4 min-h-0 flex-1',
          isCenter && showToggle
            ? 'nice-scrollbar overflow-y-auto pr-2'
            : 'overflow-hidden',
        )}
      >
        <p
          ref={isCenter ? quoteRef : undefined}
          className={cn(
            'whitespace-pre-line text-body-md leading-relaxed',
            isCenter ? 'text-on-surface' : 'text-on-surface-variant',
          )}
          style={
            isCenter
              ? isExpanded
                ? undefined
                : clampStyle(collapseLines)
              : clampStyle(5)
          }
        >
          “{display}”
        </p>
      </div>

      {/* Controls (center only) */}
      {isCenter && (showToggle || canTranslate) && (
        <div className="mt-4 flex flex-nowrap items-center gap-1.5">
          {showToggle && (
            <CardChip
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand();
              }}
            >
              <SwapLabel
                active={isExpanded ? t('showLess') : t('showMore')}
                other={isExpanded ? t('showMore') : t('showLess')}
              />
              <ChevronDown
                size={13}
                className={cn('transition-transform', isExpanded && 'rotate-180')}
              />
            </CardChip>
          )}
          {canTranslate && (
            <CardChip
              onClick={(e) => {
                e.stopPropagation();
                onToggleTranslate();
              }}
            >
              <Languages size={13} />
              <SwapLabel
                active={isTranslated ? t('showOriginal') : t('translate')}
                other={isTranslated ? t('translate') : t('showOriginal')}
              />
            </CardChip>
          )}
        </div>
      )}

      {/* Author + verified badge */}
      <div
        className={cn(
          'mt-5 flex items-center gap-3 border-t pt-4',
          isCenter ? 'border-on-surface/15' : 'border-outline-variant/30',
        )}
      >
        <span
          className={cn(
            'flex h-11 w-11 shrink-0 select-none items-center justify-center rounded-full font-headline text-body-sm font-bold',
            'bg-primary text-on-primary',
          )}
        >
          {review.initial}
        </span>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 text-label-caps uppercase tracking-widest',
            isCenter ? 'text-on-surface/75' : 'text-primary',
          )}
        >
          <ShieldCheck size={14} />
          {t('verified')}
        </span>
      </div>

      {/* Project photos (center only) → open the shared lightbox */}
      {isCenter && review.images.length > 0 && (
        <div className="mt-4 flex gap-1.5 sm:gap-2">
          {review.images.map((src, i) => (
            <ReviewThumb
              key={src}
              src={src}
              alt={`${t('thumbAlt')} ${i + 1}`}
              onPreview={onPreview}
              onClick={(e) => {
                e.stopPropagation();
                onOpenLightbox(review.images, i);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TestimonialsSection() {
  const t = useTranslations('testimonials');
  const locale = useLocale();
  const rating = t('rating');

  // Card width + fixed height adapt to the viewport.
  const [dims, setDims] = useState({ w: 380, h: 540 });
  useEffect(() => {
    const update = () =>
      setDims(
        window.matchMedia('(min-width: 640px)').matches
          ? { w: 380, h: 540 }
          : { w: 288, h: 464 },
      );
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const keyRef = useRef(REVIEWS.length);
  const [list, setList] = useState<Card[]>(() =>
    REVIEWS.map((r) => ({ ...r, key: r.id })),
  );

  // On each load, center a RANDOM photo-bearing review. Done in an effect (not the
  // initial state) so server and first client render match — no hydration drift —
  // and it runs while the section is still off-screen, so there's no visible jump.
  useEffect(() => {
    if (IMAGE_REVIEW_IDS.length === 0) return;
    const pick = IMAGE_REVIEW_IDS[Math.floor(Math.random() * IMAGE_REVIEW_IDS.length)];
    const base = keyRef.current;
    keyRef.current = base + REVIEWS.length;
    setList(rotateToCenter(pick, base));
  }, []);

  // Per-review (by id) UI state, persists as cards rotate through the center.
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [translated, setTranslated] = useState<Record<number, boolean>>({});

  const [lbImages, setLbImages] = useState<string[]>([]);
  const [lbIndex, setLbIndex] = useState(0);
  const [lbOpen, setLbOpen] = useState(false);

  // Hover preview (mouse/hover devices only — touch falls through to the
  // click→lightbox). `canHover` gates it so a tap doesn't flash a preview.
  const [preview, setPreview] = useState<Preview | null>(null);
  const canHover = useRef(false);
  useEffect(() => {
    canHover.current = window.matchMedia('(hover: hover)').matches;
  }, []);
  const showPreview = (next: Preview | null) => {
    if (next && !canHover.current) return;
    setPreview(next);
  };

  const handleMove = (steps: number) => {
    if (steps === 0) return;
    setList((prev) => {
      const next = [...prev];
      if (steps > 0) {
        for (let i = 0; i < steps; i++) {
          const item = next.shift();
          if (!item) break;
          next.push({ ...item, key: ++keyRef.current });
        }
      } else {
        for (let i = 0; i < -steps; i++) {
          const item = next.pop();
          if (!item) break;
          next.unshift({ ...item, key: ++keyRef.current });
        }
      }
      return next;
    });
  };

  // Swipe the carousel on touch devices: left → next, right → previous.
  // Fires mid-gesture (not on finger lift) so it feels immediate, and locks to
  // an axis on the first significant movement so a vertical page scroll that
  // drifts sideways never rotates the carousel.
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const swipeLocked = useRef(false); // gesture consumed, or ceded to page scroll
  const onTouchStart = (e: React.TouchEvent) => {
    swipeStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    swipeLocked.current = false;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const start = swipeStart.current;
    if (!start || swipeLocked.current) return;
    const dx = e.touches[0].clientX - start.x;
    const dy = e.touches[0].clientY - start.y;
    if (Math.abs(dy) > 24 && Math.abs(dy) > Math.abs(dx)) {
      swipeLocked.current = true; // clearly a vertical scroll — hands off
      return;
    }
    if (Math.abs(dx) > 36 && Math.abs(dx) > Math.abs(dy) * 1.2) {
      swipeLocked.current = true;
      handleMove(dx < 0 ? 1 : -1);
    }
  };
  const onTouchEnd = () => {
    swipeStart.current = null;
  };

  const openLightbox = (images: string[], index: number) => {
    setPreview(null); // the click that opens the lightbox also dismisses the hover preview
    setLbImages(images);
    setLbIndex(index);
    setLbOpen(true);
  };

  // The preview is positioned from a viewport rect, so drop it when that rect
  // goes stale: the centered review changes, or the page/carousel scrolls.
  useEffect(() => {
    if (!preview) return;
    const clear = () => setPreview(null);
    window.addEventListener('scroll', clear, { passive: true });
    return () => window.removeEventListener('scroll', clear);
  }, [preview]);

  // Resolve the card currently in the center slot (position === 0).
  const positionOf = (i: number) =>
    list.length % 2 ? i - (list.length + 1) / 2 : i - list.length / 2;
  const centerIndex = list.findIndex((_, i) => positionOf(i) === 0);
  const centerReview = list[centerIndex];
  // Rotating to a new center moves the thumbs, so the held preview rect is stale.
  useEffect(() => setPreview(null), [centerReview?.id]);
  const centerExpanded = centerReview ? !!expanded[centerReview.id] : false;
  const centerCanTranslate = !!centerReview && centerReview.lang !== locale;
  const centerTranslated =
    centerCanTranslate && !!centerReview && !!translated[centerReview.id];
  const centerDisplay = centerReview
    ? centerTranslated
      ? t(`r${centerReview.id}`)
      : centerReview.text
    : '';

  // Measure the center card's quote area (fixed height) so the collapsed clamp
  // fills it exactly with an ellipsis, and so the "show more" chip appears only
  // when text is genuinely cut off — expanding then scrolls inside, no reflow.
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const quoteRef = useRef<HTMLParagraphElement>(null);
  const [collapseLines, setCollapseLines] = useState(7);
  const [overflowing, setOverflowing] = useState(false);

  useIsoLayoutEffect(() => {
    const area = scrollAreaRef.current;
    const quote = quoteRef.current;
    if (!area || !quote) return;
    const areaH = area.clientHeight;
    const lineH = parseFloat(getComputedStyle(quote).lineHeight) || 26;
    // Clamp to the lines that FULLY fit (−2px guard) so the collapsed text never
    // overflows its area — otherwise a scrollbar shows and the "…" ends up below
    // the fold. Re-runs on `overflowing` too: showing the controls row shrinks the
    // quote area, so the line count must be recomputed for that smaller height.
    setCollapseLines(Math.max(3, Math.floor((areaH - 2) / lineH)));
    setOverflowing(quote.scrollHeight - areaH > 4);
  }, [centerReview?.id, centerDisplay, centerExpanded, overflowing, dims]);

  return (
    <section
      id="opinie"
      className="scroll-mt-2 border-t border-surface-bright/15 px-margin-mobile py-section-gap lg:scroll-mt-4 lg:px-margin-desktop"
    >
      <div className="mx-auto w-full max-w-6xl">
        <FadeInSection direction="right" distance={64}>
          {/* Eyebrow + rating on one row — the score is the first thing seen on
              reaching the section (esp. mobile), before the reviews. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="text-label-caps uppercase tracking-widest text-primary">{t('eyebrow')}</p>
            <span className="inline-flex items-center gap-1.5 font-headline text-lg font-bold leading-none text-on-surface">
              <Star size={18} className="fill-current" strokeWidth={0} style={{ color: STAR_GOLD }} />
              {rating} <span className="font-normal text-on-surface-variant">/ 5</span>
            </span>
          </div>
          <h2 className="mt-stack-md max-w-3xl font-headline text-headline-lg font-bold tracking-[0.02em] text-on-surface">
            {t('headline')}
          </h2>
          <p className="mt-stack-md max-w-2xl text-body-md text-on-surface-variant md:text-body-lg">
            {t('subhead')}
          </p>
        </FadeInSection>

        <FadeInSection direction="up" delay={0.1}>
          <div
            className="relative mt-8 w-full overflow-hidden sm:mt-12"
            style={{ height: dims.h + NAV_SPACE, touchAction: 'pan-y' }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {list.map((review, index) => {
              const position = positionOf(index);
              const isCenter = position === 0;
              const canTranslate = review.lang !== locale;
              const isTranslated = isCenter && canTranslate && !!translated[review.id];
              const display =
                isCenter && isTranslated ? t(`r${review.id}`) : review.text;
              return (
                <TestimonialCard
                  key={review.key}
                  review={review}
                  position={position}
                  cardW={dims.w}
                  cardH={dims.h}
                  display={display}
                  collapseLines={collapseLines}
                  isExpanded={isCenter && !!expanded[review.id]}
                  isTranslated={isTranslated}
                  canTranslate={isCenter && canTranslate}
                  showToggle={isCenter && (overflowing || !!expanded[review.id])}
                  t={t}
                  scrollAreaRef={scrollAreaRef}
                  quoteRef={quoteRef}
                  onSelect={() => handleMove(position)}
                  onToggleExpand={() =>
                    setExpanded((m) => ({ ...m, [review.id]: !m[review.id] }))
                  }
                  onToggleTranslate={() =>
                    setTranslated((m) => ({ ...m, [review.id]: !m[review.id] }))
                  }
                  onOpenLightbox={openLightbox}
                  onPreview={showPreview}
                />
              );
            })}

            {/* Prev / next */}
            <div className="absolute bottom-0 left-1/2 flex -translate-x-1/2 gap-3">
              <button
                type="button"
                onClick={() => handleMove(-1)}
                aria-label={t('prev')}
                className="flex h-12 w-12 items-center justify-center rounded-full border border-outline-variant bg-surface-container/80 text-on-surface backdrop-blur-md transition-colors hover:border-primary hover:bg-surface-container-high hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                type="button"
                onClick={() => handleMove(1)}
                aria-label={t('next')}
                className="flex h-12 w-12 items-center justify-center rounded-full border border-outline-variant bg-surface-container/80 text-on-surface backdrop-blur-md transition-colors hover:border-primary hover:bg-surface-container-high hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <ChevronRight size={22} />
              </button>
            </div>
          </div>

          {/* Source attribution stays quiet, under the reviews. */}
          <div className="mt-6 flex justify-center sm:mt-8">
            <p className="text-[8px] font-semibold uppercase tracking-widest text-outline">
              {t('verifiedBy')}{' '}
              <span className="text-on-surface-variant">Oferteo</span>
            </p>
          </div>
        </FadeInSection>
      </div>

      <Lightbox
        items={toImageItems(lbImages)}
        index={lbIndex}
        open={lbOpen}
        label={t('photosLabel')}
        onClose={() => setLbOpen(false)}
        onIndexChange={setLbIndex}
      />

      {preview &&
        typeof document !== 'undefined' &&
        createPortal(
          <HoverPreview
            key={preview.src}
            src={preview.src}
            rect={preview.rect}
            alt={t('photosLabel')}
          />,
          document.body,
        )}
    </section>
  );
}
